import React, { useCallback, useEffect, useMemo, useState } from 'react';
import intl from 'react-intl-universal';
import classNames from 'classnames';
import { css } from '@emotion/css';
import { MenuItem } from '@blueprintjs/core';
import { Suggest } from '@blueprintjs/select';

import { CLASSES } from '@/constants/classes';
import { FormattedMessage as T } from '@/components/FormattedMessage';
import type { AnalyticDimension } from '@/hooks/query/analyticAccounting';
import { ColorDot } from './CategoryChip';

/**
 * Blueprint ancre `.bp4-input-left-container` en haut avec une hauteur fixe :
 * sur un input plus haut (formulaires / toolbars), la pastille de couleur se
 * retrouve en haut au lieu d'être centrée. On force le conteneur à occuper
 * toute la hauteur et à centrer son contenu.
 */
const dotCenterFix = css`
  .bp4-input-left-container,
  .bp3-input-left-container {
    top: 0;
    bottom: 0;
    height: auto;
    display: flex;
    align-items: center;
  }
`;

/**
 * Renders the display label of an analytic (expense) category as `[CODE] Label`.
 */
export const getCategoryDisplayName = (
  dim?: AnalyticDimension | null,
): string => {
  if (!dim) return '';
  return dim.code ? `[${dim.code}] ${dim.label}` : dim.label || '';
};

interface AnalyticCategorySuggestFieldProps {
  /** List of EXPENSE_CATEGORY dimensions. */
  categories: AnalyticDimension[];
  /** Currently selected dimension id (controlled). */
  selectedCategoryId?: number | null;
  /** Initial dimension id (uncontrolled first render). */
  initialCategoryId?: number | null;
  defaultSelectText?: string;
  popoverFill?: boolean;
  /** Called with the picked dimension object. */
  onCategorySelected?: (category: AnalyticDimension) => void;
  disabled?: boolean;
  [key: string]: any;
}

/**
 * Unified autocomplete picker for an analytic category (EXPENSE_CATEGORY
 * dimension). Filters on both label and code, displays `[CODE] Label`.
 * Modelled on ProjectSuggestField / AccountsSuggestField — never an ID input.
 */
export function AnalyticCategorySuggestField({
  categories = [],
  selectedCategoryId,
  initialCategoryId,
  defaultSelectText = intl.get('analytic_category.select') ||
    'Sélectionner une catégorie',
  popoverFill = false,
  onCategorySelected,
  disabled = false,
  ...suggestProps
}: AnalyticCategorySuggestFieldProps) {
  const initialCategory = useMemo(
    () => categories.find((c: any) => c.id === initialCategoryId) || null,
    [initialCategoryId, categories],
  );

  const [selectedCategory, setSelectedCategory] = useState(
    initialCategory || null,
  );
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (typeof selectedCategoryId !== 'undefined') {
      const category = selectedCategoryId
        ? categories.find((c: any) => c.id === Number(selectedCategoryId)) ||
          null
        : null;
      setSelectedCategory(category);
    }
  }, [selectedCategoryId, categories]);

  const itemRenderer = useCallback(
    (category: any, { handleClick, modifiers }: any) => {
      if (!modifiers.matchesPredicate) return null;
      return (
        <MenuItem
          active={modifiers.active}
          disabled={modifiers.disabled}
          key={category.id}
          icon={<ColorDot color={category.color} />}
          label={category.code}
          text={category.label}
          onClick={handleClick}
        />
      );
    },
    [],
  );

  const itemPredicate = useCallback(
    (query: any, category: any, _index: any, exactMatch: any) => {
      const label = (category.label || '').toLowerCase();
      const code = (category.code || '').toLowerCase();
      const q = query.toLowerCase().trim();
      if (!q) return true;
      if (exactMatch) {
        return label === q || code === q;
      }
      return label.indexOf(q) >= 0 || code.indexOf(q) >= 0;
    },
    [],
  );

  const handleSelect = useCallback(
    (category: any) => {
      if (category?.id) {
        setSelectedCategory({ ...category });
        onCategorySelected && onCategorySelected(category);
      }
      setIsOpen(false);
    },
    [onCategorySelected],
  );

  const handleInputClick = useCallback(() => {
    if (!disabled) setIsOpen(true);
  }, [disabled]);

  const inputValueRenderer = useCallback(
    (category: any) => getCategoryDisplayName(category),
    [],
  );

  const mergedInputProps = {
    placeholder: defaultSelectText,
    onClick: handleInputClick,
    disabled,
    // Colour dot of the selected category, shown inside the input so the field
    // reads as a colored chip (not just text).
    leftElement: selectedCategory ? (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 28,
        }}
      >
        <ColorDot color={selectedCategory.color} size={9} />
      </span>
    ) : undefined,
    ...suggestProps.inputProps,
  };

  const mergedPopoverProps = {
    minimal: true,
    boundary: 'window',
    isOpen: isOpen && !disabled,
    onInteraction: (nextOpenState: any) => setIsOpen(nextOpenState),
    ...suggestProps.popoverProps,
  };

  return (
    <Suggest
      items={categories}
      noResults={
        <MenuItem
          disabled={true}
          text={<T id={'analytic_category.no_results'} />}
        />
      }
      itemRenderer={itemRenderer}
      itemPredicate={itemPredicate}
      onItemSelect={handleSelect}
      selectedItem={selectedCategory}
      inputProps={mergedInputProps}
      resetOnClose={true}
      fill={true}
      disabled={disabled}
      popoverProps={mergedPopoverProps}
      inputValueRenderer={inputValueRenderer}
      className={classNames(dotCenterFix, CLASSES.FORM_GROUP_LIST_SELECT, {
        [CLASSES.SELECT_LIST_FILL_POPOVER]: popoverFill,
      })}
      {...suggestProps}
    />
  );
}
